import React, { FunctionComponent, ReactNode, useState } from "react";
import { CircularProgress, Box, Checkbox, FormControlLabel } from "@mui/material";
import Counter from "./Counter";
import { healthInfoUserViewService, FIXED_TYPE_IDS } from "src/api/medcards/healthInfoUserViewService";
import { useAppSelector } from "src/app/hooks";
import storage from "src/utils/storage/cryptoStorage";
import ByzDeleteConfirmDialog from "src/components/core-components/mui/ByzDeleteConfirmDialog";
import ByzMuiSnackBar from "src/components/core-components/mui/ByzMuiSnackBar";

interface OpenCloseSectionProps {
  title: string;
  isOpen: boolean;
  onToggle?: () => void;
  showAddButton?: boolean;
  onAddClick?: () => void;
  showSaveButton?: boolean;
  onSaveClick?: () => void;
  saveLoading?: boolean;
  count?: number;
  children?: ReactNode;
  showInMainPanel?: boolean;
  onShowInMainPanelChange?: (checked: boolean) => void;
  typeId?: number; // HealthInfoViewType ID'si
}

const OpenCloseSection: FunctionComponent<OpenCloseSectionProps> = ({
  title,
  isOpen,
  onToggle,
  showAddButton = false,
  onAddClick,
  showSaveButton = false,
  onSaveClick,
  saveLoading = false,
  count,
  children,
  showInMainPanel = false,
  onShowInMainPanelChange,
  typeId,
}) => {
  const { user } = useAppSelector((state: any) => state.auth);
  const [checkboxLoading, setCheckboxLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pendingDeleteTypeId, setPendingDeleteTypeId] = useState<number | null>(null);

  // Snackbar states
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarType, setSnackbarType] = useState<"success" | "error">("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // Kullanıcı ID'sini Redux state'den al
  const getUserId = (): number | null => {
    return user?.id || null;
  };

  // Kullanıcı adını storage'dan al
  const getUsername = (): string => {
    return storage.getLocalStorage('username') || 'system';
  };

  // Checkbox değişikliğini handle et
  const handleCheckboxChange = async (checked: boolean) => {
    if (!typeId) {
      console.warn('⚠️ TypeId tanımlı değil, checkbox değişikliği API\'ye gönderilemiyor');
      onShowInMainPanelChange?.(checked);
      return;
    }

    // Sabit tipler değiştirilemez
    if (FIXED_TYPE_IDS.includes(typeId)) {
      console.warn(`⚠️ TypeId ${typeId} sabit bir tip, değiştirilemez`);
      return;
    }

    // Checkbox kaldırılıyorsa onay dialogu göster
    if (!checked) {
      setPendingDeleteTypeId(typeId);
      setDeleteDialogOpen(true);
      return;
    }

    // Checkbox işaretleniyorsa direkt ekle
    await handleAdd(typeId);
  };

  // Tip ekleme işlemi
  const handleAdd = async (addTypeId: number) => {
    const userId = getUserId();
    if (!userId) {
      console.error('❌ Kullanıcı ID bulunamadı');
      setSnackbarType("error");
      setSnackbarMessage("Kullanıcı bilgisi bulunamadı");
      setSnackbarOpen(true);
      return;
    }

    try {
      setCheckboxLoading(true);

      await healthInfoUserViewService.addHealthInfoViewForUser({
        userId,
        cuser: getUsername(),
        typeId: addTypeId,
      });


      // Başarılı olursa parent component'e bildir
      onShowInMainPanelChange?.(true);

      // Başarı mesajı göster
      setSnackbarType("success");
      setSnackbarMessage("Tercih başarıyla eklendi");
      setSnackbarOpen(true);
    } catch (error) {
      console.error(`❌ Tip eklenemedi (TypeId: ${addTypeId}):`, error);
      setSnackbarType("error");
      setSnackbarMessage("Tercih eklenirken bir hata oluştu");
      setSnackbarOpen(true);
    } finally {
      setCheckboxLoading(false);
    }
  };

  // Tip silme işlemi (delete dialog'dan onaylandıktan sonra)
  const handleDeleteConfirm = async (): Promise<boolean> => {
    if (!pendingDeleteTypeId) return false;

    const userId = getUserId();
    if (!userId) {
      console.error('❌ Kullanıcı ID bulunamadı');
      return false;
    }

    try {
      await healthInfoUserViewService.deleteHealthInfoViewForUser(userId, pendingDeleteTypeId);

      // Başarılı olursa parent component'e bildir
      onShowInMainPanelChange?.(false);

      return true;
    } catch (error) {
      console.error(`❌ Tip silinemedi (TypeId: ${pendingDeleteTypeId}):`, error);
      return false;
    } finally {
      setPendingDeleteTypeId(null);
    }
  };

  // Sabit tip kontrolü
  const isFixedType = typeId ? FIXED_TYPE_IDS.includes(typeId) : false;

  // Checkbox label'ını belirle
  const getCheckboxLabel = () => {
    if (checkboxLoading) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={14} />
          <span>Kaydediliyor...</span>
        </Box>
      );
    }
    return "Ana Panelde Her Zaman Gösterilsin";
  };

  return (
    <>
      <Box
        sx={{
          width: "100%",
          height: "53px",
          position: "relative",
          backgroundColor: "#ffffff",
          borderBottom: "1px solid #e0e5eb",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          justifyContent: "center",
          padding: "12px 16px",
          textAlign: "left",
          fontSize: "16px",
          color: "#0b0d1e",
          fontFamily:
            'Poppins, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          "&:hover": {
            backgroundColor: "#f7f9fc",
          },
          cursor: onToggle ? "pointer" : "default",
        }}
        onClick={onToggle}
      >
        <Box sx={{ alignSelf: "stretch", display: "flex", alignItems: "center", gap: "8px" }}>
          <Box
            component="img"
            sx={{
              height: "16px",
              width: "16px",
              objectFit: "contain",
              flexShrink: 0,
            }}
            alt={isOpen ? "Kapat" : "Aç"}
            src={isOpen ? "/assets/icons/nurse/close.svg" : "/assets/icons/nurse/open.svg"}
          />
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: "10px" }}>
            <Box
              component="b"
              sx={{
                position: "relative",
                letterSpacing: "-0.02em",
                lineHeight: "24px",
                fontWeight: 600,
              }}
            >
              {title}
            </Box>
            {typeof count === "number" && <Counter value={count} />}
          </Box>
        </Box>
      </Box>

      {isOpen && (
        <Box
          sx={{
            width: "100%",
            position: "relative",
            backgroundColor: "#f6f6f9",
            borderBottom: "1px solid #ff0004",
            boxSizing: "border-box",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            padding: "16px",
            textAlign: "left",
            fontSize: "16px",
            color: "#2979c3",
            fontFamily:
              'Poppins, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
          }}
        >
          {/* Butonlar ve Checkbox aynı satırda */}
          <Box
            sx={{
              alignSelf: "stretch",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: (showAddButton || showSaveButton) ? "12px" : "0px",
              gap: "16px"
            }}
          >
            {/* Sol taraf: Ekle ve Kaydet butonları */}
            <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
              {showAddButton && (
                <Box
                  sx={{
                    width: "79px",
                    borderRadius: "20px",
                    backgroundColor: "rgba(99, 180, 255, 0.2)",
                    border: "1px solid #2979c3",
                    boxSizing: "border-box",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "4px 10px",
                    cursor: "pointer",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onAddClick?.();
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                    <Box sx={{ position: "relative", textTransform: "capitalize", flexShrink: 0 }}>
                      Ekle
                    </Box>
                  </Box>
                </Box>
              )}

              {showSaveButton && (
                <Box
                  sx={{
                    width: "79px",
                    borderRadius: "20px",
                    backgroundColor: "rgba(99, 180, 255, 0.2)",
                    border: "1px solid #2979c3",
                    boxSizing: "border-box",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "4px 10px",
                    cursor: "pointer",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onSaveClick?.();
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                    {saveLoading ? (
                      <CircularProgress size={16} sx={{ color: "#2D9435" }} />
                    ) : (
                      <Box sx={{ position: "relative", textTransform: "capitalize", flexShrink: 0 }}>
                        Kaydet
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </Box>

            {/* Sağ taraf: Ana Panelde Her Zaman Gösterilsin Checkbox */}
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showInMainPanel || isFixedType}
                    onChange={(e) => handleCheckboxChange(e.target.checked)}
                    size="small"
                    sx={{ padding: "4px" }}
                    disabled={isFixedType || checkboxLoading}
                  />
                }
                label={getCheckboxLabel()}
                sx={{
                  margin: 0,
                  fontSize: "13px",
                  '& .MuiFormControlLabel-label': {
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#333",
                  }
                }}
              />
            </Box>
          </Box>

          {children && <Box sx={{ width: "100%" }}>{children}</Box>}
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <ByzDeleteConfirmDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setPendingDeleteTypeId(null);
        }}
        onDelete={handleDeleteConfirm}
        title="Tercihi Kaldır"
        content="Bu sağlık bilgisi tipini ana panelden kaldırmak istediğinizden emin misiniz?"
        successMessage="Tercih başarıyla kaldırıldı"
        errorMessage="Tercih kaldırılırken bir hata oluştu"
      />

      {/* Snackbar for notifications */}
      <ByzMuiSnackBar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        type={snackbarType}
        title={snackbarType === "success" ? "Başarılı" : "Hata"}
        message={snackbarMessage}
      />
    </>
  );
};

export default OpenCloseSection;
